import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap'
import { Link, useSearchParams } from 'react-router-dom'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { formatTimeShort } from '../utils/teachingUtils'

// หน้ารายละเอียดที่กดมาจากการ์ด "สถานะการเช็คชื่อวันนี้" บนหน้าแรก
// แสดงเฉพาะ ขาด/สาย/ลา — "มาเรียน" ไม่มีอะไรให้ตาม จึงไม่มีในหน้านี้
const STATUSES = [
  { key: 'absent', label: 'ขาดเรียน', color: 'danger',  icon: 'ri-user-unfollow-line' },
  { key: 'late',   label: 'สาย',      color: 'warning', icon: 'ri-time-line' },
  { key: 'leave',  label: 'ลา',       color: 'primary', icon: 'ri-file-list-3-line' },
]
const STATUS_KEYS = STATUSES.map(s => s.key)

const GENDER_LABEL = { m: 'ชาย', f: 'หญิง' }

const formatDateTH = (d) =>
  d ? new Date(d).toLocaleDateString('th-TH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ''

// c_class ภาคสามัญเป็นข้อความเต็ม ("ชั้นมัธยมศึกษาปีที่ 2") ย่อเป็น "ม.2" ให้เหมือนที่อื่นในระบบ
// ภาคศาสนา ic_class เป็นสั้นอยู่แล้ว ("2", "AIP1")
const classLabel = (row, section) => {
  if (row.c_class == null) return '-'
  if (section === 'islamic') return String(row.c_class)
  const level = String(row.c_class).replace(/[^0-9]/g, '')
  return level ? `ม.${level}` : row.c_class
}
const roomLabel = (row, section) => `${classLabel(row, section)}/${row.r_no ?? '-'}`

const periodLabel = (row) => {
  if (row.period_order == null) return '-'
  const time = row.period_begin && row.period_end
    ? ` (${formatTimeShort(row.period_begin)}-${formatTimeShort(row.period_end)})`
    : ''
  return `คาบ ${row.period_order}${time}`
}

export default function TodayAttendanceDetail({ section = 'general' }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [data, setData] = useState({ rows: [], today: '' })

  const initialStatus = STATUS_KEYS.includes(searchParams.get('status')) ? searchParams.get('status') : 'absent'
  const [status, setStatus] = useState(initialStatus)
  const [gender, setGender] = useState('')
  const [classId, setClassId] = useState('')
  const [periodId, setPeriodId] = useState('')

  useEffect(() => {
    setLoading(true)
    setError(false)
    axiosInstance.get('/api/getTodayAttendanceDetail', { params: { section } })
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Today attendance detail error:', err)
        setError(true)
      })
      .finally(() => setLoading(false))
  }, [section])

  const rows = useMemo(() => data.rows ?? [], [data])

  // ยอดต่อสถานะ คิดจากทั้งชุดก่อนกรอง เพื่อให้ปุ่มสถานะแสดงยอดเดียวกับหน้าแรก
  const statusCounts = useMemo(() => {
    const c = { absent: 0, late: 0, leave: 0 }
    rows.forEach(r => { if (c[r.att_status] != null) c[r.att_status] += 1 })
    return c
  }, [rows])

  // ตัวเลือกตัวกรองมาจากข้อมูลที่มีจริงในวันนี้ ไม่ต้องยิง API เพิ่ม
  const classOptions = useMemo(() => {
    const map = new Map()
    rows.forEach(r => { if (r.c_id != null && !map.has(r.c_id)) map.set(r.c_id, classLabel(r, section)) })
    return [...map.entries()].map(([id, label]) => ({ id: String(id), label }))
  }, [rows, section])

  const periodOptions = useMemo(() => {
    const map = new Map()
    rows.forEach(r => { if (r.period_id != null && !map.has(r.period_id)) map.set(r.period_id, r) })
    return [...map.values()]
      .sort((a, b) => (a.period_order ?? 0) - (b.period_order ?? 0))
      .map(r => ({ id: String(r.period_id), label: periodLabel(r) }))
  }, [rows])

  const filtered = useMemo(() => rows.filter(r =>
    r.att_status === status
    && (!gender || r.st_gen === gender)
    && (!classId || String(r.c_id) === classId)
    && (!periodId || String(r.period_id) === periodId)
  ), [rows, status, gender, classId, periodId])

  const distinctStudents = useMemo(() => new Set(filtered.map(r => r.st_id)).size, [filtered])

  const changeStatus = (key) => {
    setStatus(key)
    setSearchParams({ status: key }, { replace: true })
  }

  const clearFilters = () => { setGender(''); setClassId(''); setPeriodId('') }
  const hasFilter = gender || classId || periodId

  const current = STATUSES.find(s => s.key === status)
  const sectionLabel = section === 'islamic' ? 'ภาคศาสนา' : 'ภาคสามัญ'

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
            <div>
              <ol className="breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><Link to="/dashboard/school-statistic">หน้าแรก</Link></li>
                <li className="breadcrumb-item active" aria-current="page">รายละเอียดการเช็คชื่อวันนี้</li>
              </ol>
              <h4 className="main-title mb-0">
                <i className="ri-calendar-check-line me-2 text-primary" />
                รายละเอียดการเช็คชื่อวันนี้ — {sectionLabel}
              </h4>
              {data.today && <div className="text-secondary fs-sm mt-1">{formatDateTH(data.today)}</div>}
            </div>
            <Button as={Link} to="/dashboard/school-statistic" variant="white" size="sm">
              <i className="ri-arrow-left-line me-1" />กลับหน้าแรก
            </Button>
          </div>

          {/* ปุ่มเลือกสถานะ พร้อมยอดรวมของวันนี้ */}
          <Row className="g-3 mb-3">
            {STATUSES.map(s => {
              const active = s.key === status
              return (
                <Col key={s.key} xs="12" sm="4">
                  <Card
                    role="button"
                    onClick={() => changeStatus(s.key)}
                    className={`card-one h-100 border-2 ${active ? `border-${s.color}` : 'border-transparent'}`}
                  >
                    <Card.Body className="p-3 d-flex align-items-center gap-3">
                      <div className={`flex-shrink-0 rounded-3 d-flex align-items-center justify-content-center bg-${s.color} bg-opacity-10 text-${s.color}`} style={{ width: 44, height: 44 }}>
                        <i className={`${s.icon} fs-20`} />
                      </div>
                      <div>
                        <div className={`fw-bold fs-20 lh-1 text-${s.color}`}>{statusCounts[s.key].toLocaleString()}</div>
                        <div className="fs-sm text-secondary">{s.label}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )
            })}
          </Row>

          <Card className="card-one">
            <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <Card.Title as="h6" className="mb-0 d-flex align-items-center gap-2">
                <Badge bg={current.color} text={current.color === 'warning' ? 'dark' : undefined}>{current.label}</Badge>
                <span>
                  {filtered.length.toLocaleString()} รายการ
                  <span className="text-secondary fw-normal"> · {distinctStudents.toLocaleString()} คน</span>
                </span>
              </Card.Title>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Form.Select size="sm" style={{ width: 130 }} value={gender} onChange={e => setGender(e.target.value)}>
                  <option value="">ทุกเพศ</option>
                  <option value="m">ชาย</option>
                  <option value="f">หญิง</option>
                </Form.Select>
                <Form.Select size="sm" style={{ width: 150 }} value={classId} onChange={e => setClassId(e.target.value)}>
                  <option value="">ทุกชั้น</option>
                  {classOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </Form.Select>
                <Form.Select size="sm" style={{ width: 200 }} value={periodId} onChange={e => setPeriodId(e.target.value)}>
                  <option value="">ทุกคาบ</option>
                  {periodOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </Form.Select>
                {hasFilter && (
                  <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                    <i className="ri-close-line me-1" />ล้างตัวกรอง
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" size="sm" className="me-2" />กำลังโหลด...</div>
              ) : error ? (
                <div className="text-center text-danger py-5">
                  <i className="ri-error-warning-line fs-40 d-block mb-2" />ไม่สามารถโหลดข้อมูลได้
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center text-secondary py-5">
                  <i className="ri-inbox-2-line fs-40 mb-2 opacity-25 d-block" />
                  <p className="fs-sm mb-0">
                    {rows.length === 0 ? 'ยังไม่มีข้อมูลการเช็คชื่อวันนี้' : `ไม่พบรายการ${current.label}ตามเงื่อนไขที่เลือก`}
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle fs-sm">
                    <thead>
                      <tr>
                        <th className="text-center" style={{ width: 50 }}>#</th>
                        <th>เลขประจำตัว</th>
                        <th>ชื่อ-สกุล</th>
                        <th className="text-center">เพศ</th>
                        <th className="text-center">ห้อง</th>
                        <th>คาบ</th>
                        <th>วิชา</th>
                        <th>ครูผู้สอน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r, i) => (
                        <tr key={r.att_id}>
                          <td className="text-center text-secondary">{i + 1}</td>
                          <td>{r.st_idnumber}</td>
                          <td>
                            <div className="fw-semibold">{r.st_name}</div>
                            {section === 'islamic' && r.st_arabicname && (
                              <div className="fs-xs text-secondary">{r.st_arabicname}</div>
                            )}
                          </td>
                          <td className="text-center">
                            <i className={r.st_gen === 'f' ? 'ri-women-line text-danger' : 'ri-men-line text-info'} /> {GENDER_LABEL[r.st_gen] ?? '-'}
                          </td>
                          <td className="text-center">{roomLabel(r, section)}</td>
                          <td>{periodLabel(r)}</td>
                          <td>{r.sub_subname ?? '-'}</td>
                          <td>{r.teacher_name ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
        <Footer />
      </div>
    </>
  )
}
